// Fight-stance idle candidates (owner 2026-11-03: "Give hero a really cool resting fight stance"). NOT LIVE: the default
// idle is unchanged until the owner picks one (CLAUDE.md "Animation previews before applying").
//
// A stance is a procedural layer on top of the authored GLB idle clip, written against bone NAMES (the 43-bone rig), so it
// carries over to any hero GLB with that skeleton. Each candidate is data: bone-local axis-angle offsets (the pose) plus
// idle life (breath, weight shift, bob, a micro-adjustment every few seconds, cloth sway + a settle when he stops).
// Blending: the layer's weight is the idle clip's share of the mixer's blend, lower and upper body separately, so every
// crossfade the mixer already does (walk/run, strikes, Nova Lance, Surge, level-up clench, flinch, defeat) fades the stance
// with it and nothing pops. Feet: the stance bends the legs; the ground-contact plant in heroGltf lowers the body so the
// lowest ankle stays at its rest height (that is what makes a low stance low). Presentation only (never touches the sim).
//
// Preview: ?animlab&mode=stance&keys=current,a,b,c,d&cam=three|side|front|game (or ?animlab&a=current&b=a).
// In game: ?idle=a|b|c|d. Default: 'current' (the plain idle clip).
import { Quaternion, Vector3, type Bone, type Object3D } from 'three/webgpu';

/** one bone-local rotation (axis in the bone's local frame, radians); the angle scales with the blend weight */
export interface StanceOp { bone: string; axis: [number, number, number]; angle: number }
export type Side = 'L' | 'R';
export interface StanceLife {
  /** extra breathing on top of the clip's (1 = subtle) */ breath: number;
  /** slow weight shift between the feet */ sway: number;
  /** knee pump "ready to spring" bob: amplitude (1 = ~1.5 cm) and rate (Hz) */ bob: number; bobHz: number;
  /** micro-adjustments (fist re-clench, shoulder roll, glance, hand flex) strength */ fidget: number;
  /** cape / scarf sway and settle strength */ cloth: number;
}
export interface Stance {
  label: string; note: string;
  pose: StanceOp[];
  /** pelvis shift (m, hero frame: +x = his left, +z = forward) — puts the weight over a foot; height comes from the plant */
  shift?: [number, number, number];
  life: StanceLife;
  /** faint energy glow on this hand (presentation: a small additive sprite parented to the hand bone) */
  glow: Side | null;
}

// ---- authoring vocabulary (the rig's bone axes are rolled; these map intent onto measured local axes) ----
// Hero faces +Z, his left is +X. Measured with the sensitivity probe on hero_hy3d_aren.glb (same skeleton as aren.glb).
const S2 = Math.SQRT1_2;
const o = (bone: string, axis: [number, number, number], angle: number): StanceOp => ({ bone, axis, angle });
const nz = (ops: StanceOp[]) => ops.filter((x) => x.angle !== 0);
/** torso chain bone (hips, spine, chest, neck, head): pitch + = lean/nod forward, yaw + = turn to his left, roll + = tilt to his right */
const torso = (bone: string, pitch: number, yaw = 0, roll = 0) => nz([o(bone, [1, 0, 0], pitch), o(bone, [0, 1, 0], yaw), o(bone, [0, 0, 1], roll)]);
/** upper arm: fwd + = swing forward/up, out + = raise sideways, twist + = rotate the arm inward */
const arm = (s: Side, fwd: number, out = 0, twist = 0) => { const mz = s === 'L' ? -S2 : S2; return nz([o(`upperarm.${s}`, [S2, 0, mz], out), o(`upperarm.${s}`, [-S2, 0, mz], fwd), o(`upperarm.${s}`, [0, s === 'L' ? 1 : -1, 0], twist)]); };
/** forearm: flex + = bend the elbow (hand comes up/forward), twist = pronate/supinate */
const fore = (s: Side, flex: number, twist = 0) => { const mz = s === 'L' ? -S2 : S2; return nz([o(`forearm.${s}`, [-S2, 0, mz], flex), o(`forearm.${s}`, [0, s === 'L' ? 1 : -1, 0], twist)]); };
/** hand: bend + = palm flexes toward the forearm, side + = ulnar bend, twist = turn the palm */
const hand = (s: Side, bend: number, side = 0, twist = 0) => { const mz = s === 'L' ? -S2 : S2; return nz([o(`hand.${s}`, [-S2, 0, mz], bend), o(`hand.${s}`, [S2, 0, mz], side), o(`hand.${s}`, [0, s === 'L' ? 1 : -1, 0], twist)]); };
/** fingers + thumb: curl + = close toward a fist, − = open the hand */
const curl = (s: Side, k: number) => { const mz = s === 'L' ? -S2 : S2; return nz([o(`fingers.${s}`, [-S2, 0, mz], k), o(`thumb.${s}`, [-S2, 0, mz], k * 0.6)]); };
/** thigh: fwd + = leg forward, out + = leg out to the side, toe + = toes turn outward (away from the body) */
const leg = (s: Side, fwd: number, out = 0, toe = 0) => nz([o(`thigh.${s}`, [0, 1, 0], s === 'L' ? -toe : toe), o(`thigh.${s}`, [0, 0, 1], s === 'L' ? -out : out), o(`thigh.${s}`, [1, 0, 0], -fwd)]);
/** shin: flex + = bend the knee */
const shin = (s: Side, flex: number) => nz([o(`shin.${s}`, [1, 0, 0], flex)]);
/** foot: down + = toes down (plantar flex), roll + = sole tilts (outer edge down for L) */
const foot = (s: Side, down: number, roll = 0) => nz([o(`foot.${s}`, [1, 0, 0], down), o(`foot.${s}`, [0, 0, 1], s === 'L' ? -roll : roll)]);
/** shoulder bone: lift + = shrug up, back + = pull the shoulder back */
const shoulder = (s: Side, lift: number, back = 0) => nz([o(`shoulder.${s}`, [1, 0, 0], lift), o(`shoulder.${s}`, [0, 0, 1], s === 'L' ? back : -back)]);
const F = (ops: StanceOp[][]) => ops.flat();
/** the vocabulary, for the tuner script and tests */
export const POSE = { torso, arm, fore, hand, curl, leg, shin, foot, shoulder };

export const STANCES: Record<string, Stance> = {
  current: {
    label: 'Current idle (live)', note: 'The authored idle clip as it ships: upright, arms at the sides, breath only.',
    pose: [], life: { breath: 0, sway: 0, bob: 0, bobHz: 0, fidget: 0, cloth: 0 }, glow: null,
  },
  a: {
    label: 'A · Grounded guard', note: 'Wide, low, side-on. Lead hand open and forward, rear fist chambered at the hip, weight back, chin down.',
    shift: [-0.06, 0, -0.10],
    pose: F([
      torso('hips', 0.16, -0.72, 0.03), torso('spine', 0.08, 0.12), torso('chest', 0.06, 0.22, -0.02), torso('neck', 0.06, 0.14), torso('head', 0.14, 0.24, -0.04),
      leg('L', 0.99, 0.25, 1.00), shin('L', 0.60), foot('L', 0.16, -0.53),
      leg('R', 0.73, 0.33, 1.03), shin('R', 0.77), foot('R', -0.20, -0.66),
      shoulder('L', 0.02, -0.06), arm('L', -0.07, 0.90, -0.57), fore('L', 1.33, 0.1), hand('L', -0.15, 0, 0.3), curl('L', -0.35),
      shoulder('R', 0.04, 0.10), arm('R', -0.21, -0.23, 0.21), fore('R', 1.07, -0.2), hand('R', 0.15), curl('R', 0.75),
    ]),
    life: { breath: 1, sway: 0.8, bob: 0.3, bobHz: 0.9, fidget: 1, cloth: 1 }, glow: null,
  },
  b: {
    label: 'B · Ready fighter', note: 'Both fists up and loose, lead shoulder forward, light bounce, small weight shifts.',
    pose: F([
      torso('hips', 0.06, -0.42), torso('spine', 0.05, 0.08), torso('chest', 0.06, 0.14), torso('neck', 0.06, 0.1), torso('head', 0.12, 0.10, 0.03),
      leg('L', 0.52, 0.20, 0.40), shin('L', 0.39), foot('L', 0.05, -0.30),
      leg('R', 0.24, 0.32, 0.66), shin('R', 0.34), foot('R', -0.22, -0.51),
      shoulder('L', 0.06, -0.04), arm('L', 0.26, -0.07, 0.09), fore('L', 2.01, 0.15), hand('L', 0.25, 0, 0.3), curl('L', 0.85),
      shoulder('R', 0.08, 0.02), arm('R', 0.13, -0.56, 0.35), fore('R', 1.84, 0.1), hand('R', 0.25, 0, 0.3), curl('R', 0.85),
    ]),
    life: { breath: 0.8, sway: 1.2, bob: 0.8, bobHz: 1.9, fidget: 1.3, cloth: 0.8 }, glow: null,
  },
  c: {
    label: 'C · Cocky charge-ready', note: 'Relaxed but dangerous: weight on the back leg, one hand low with a faint glow, the other by the chest, head tilted.',
    shift: [-0.05, 0, -0.12],
    pose: F([
      torso('hips', -0.02, -0.30, -0.04), torso('spine', 0.0, 0.06), torso('chest', -0.04, 0.10, 0.03), torso('neck', 0.0, 0.08), torso('head', -0.06, 0.06, 0.18),
      leg('L', 0.52, 0.19, 0.31), shin('L', 0.24), foot('L', 0.32, -0.17),
      leg('R', 0.28, 0.20, 0.66), shin('R', 0.49), foot('R', -0.21, -0.12),
      shoulder('L', 0.03, -0.02), arm('L', 0.24, -0.19, 0.15), fore('L', 1.14, 0.3), hand('L', 0.1, 0, 0.4), curl('L', 0.55),
      shoulder('R', -0.03, 0.04), arm('R', 0.19, 0.04, 0.01), fore('R', 0.10, 0.2), hand('R', -0.25, 0, -0.2), curl('R', -0.25),
    ]),
    life: { breath: 1.1, sway: 1.4, bob: 0, bobHz: 0, fidget: 0.7, cloth: 1.3 }, glow: 'R',
  },
  d: {
    label: 'D · Coiled tiger', note: 'Deep, wide, almost crouching horse stance. Lead open hand forward at head height, rear fist chambered low, a slight bob as if ready to spring.',
    shift: [-0.03, 0, -0.06],
    pose: F([
      torso('hips', 0.26, -0.68, 0.04), torso('spine', 0.12, 0.12), torso('chest', 0.08, 0.20, -0.03), torso('neck', 0.02, 0.12), torso('head', 0.08, 0.24, -0.05),
      leg('L', 1.36, 0.16, 1.14), shin('L', 0.91), foot('L', 0.13, -0.57),
      leg('R', 1.00, 0.28, 1.26), shin('R', 0.92), foot('R', -0.08, -0.79),
      shoulder('L', 0.05, -0.08), arm('L', -0.13, 0.70, -0.61), fore('L', 1.65, 0.1), hand('L', -0.25, 0, 0.35), curl('L', 0.10),
      shoulder('R', 0.08, 0.14), arm('R', -0.10, -0.15, 0.15), fore('R', 0.63, -0.2), hand('R', 0.2), curl('R', 0.85),
    ]),
    life: { breath: 1.2, sway: 0.6, bob: 1.0, bobHz: 1.4, fidget: 1.1, cloth: 1 }, glow: null,
  },
};
/** the idle the game uses: owner 2026-11-03 'Grounded guard is default' (A); ?idle=current gives the old idle */
export const ACTIVE_STANCE = 'a';
/** ?idle=a|b|c|d (game) — anything else is the live idle */
export function stanceFromParams(params: URLSearchParams): string {
  const k = params.get('idle') ?? '';
  return STANCES[k] ? k : ACTIVE_STANCE;
}

const _q = new Quaternion(), _ax = new Vector3();
const LOWER = /^(hips|thigh|shin|foot|toe)/;
/** tiny deterministic hash → 0..1 (the lab's recordings stay repeatable) */
const h01 = (n: number) => { let x = (n * 2654435761 + 1013904223) >>> 0; x ^= x >>> 13; x = Math.imul(x, 0x5bd1e995) >>> 0; x ^= x >>> 15; return (x >>> 0) / 4294967296; };

/** Applies a Stance on top of the mixer's result each frame (save after the mixer, restore before the next update). */
export class StanceLayer {
  private bones = new Map<string, Bone>();
  private saved: Array<[Bone, Quaternion]> = [];
  private hips: Bone | null = null; private hipsP = new Vector3();
  private t = 0; private on = 0;
  private settleT = 9; private lastLo = 0;
  private fidgetAt = 2.2; private fidgetN = 0; private fidgetK = 1; private fidgetKind = 0;
  /** last applied weights (lower, upper) for the glow / QA */
  lo = 0; up = 0;
  stance: Stance | null = null;

  constructor(root: Object3D, seed = 0) {
    root.traverse((b) => { if ((b as Bone).isBone) { this.bones.set(b.name, b as Bone); this.saved.push([b as Bone, new Quaternion().copy(b.quaternion)]); } });
    this.hips = this.bone('hips') ?? null; if (this.hips) this.hipsP.copy(this.hips.position);
    this.fidgetN = Math.floor(seed * 97);
  }
  bone(name: string): Bone | undefined { return this.bones.get(name.replace(/\./g, '')); }
  /** before mixer.update: put back the mixer's own result (three's PropertyMixer skips bones whose blend did not change) */
  restore(): void { for (const [b, q] of this.saved) b.quaternion.copy(q); if (this.hips) this.hips.position.copy(this.hipsP); }
  /** right after mixer.update */
  save(): void { for (const [b, q] of this.saved) q.copy(b.quaternion); if (this.hips) this.hipsP.copy(this.hips.position); }

  private rot(name: string, axis: [number, number, number], angle: number): void {
    if (Math.abs(angle) < 1e-5) return;
    const b = this.bone(name); if (!b) return;
    b.quaternion.multiply(_q.setFromAxisAngle(_ax.set(axis[0], axis[1], axis[2]).normalize(), angle));
  }
  private ops(ops: StanceOp[], lo: number, up: number): void { for (const op of ops) this.rot(op.bone, op.axis, op.angle * (LOWER.test(op.bone) ? lo : up)); }

  /**
   * @param lo the idle clip's share of the lower-body blend (0..1), @param up the same for the upper body.
   * Returns the applied (lower, upper) weights.
   */
  apply(dt: number, lo: number, up: number): void {
    const S = this.stance;
    this.on += ((S ? 1 : 0) - this.on) * Math.min(1, dt * 3); // ramps in after load so the plant's rest height is the plain idle
    lo = Math.max(0, Math.min(1, lo)) * this.on; up = Math.max(0, Math.min(1, up)) * this.on;
    this.lo = lo; this.up = up;
    if (!S || (lo < 1e-3 && up < 1e-3)) { this.lastLo = lo; return; }
    this.t += dt;
    const t = this.t, L = S.life;
    if (S.shift && this.hips) this.hips.position.add(_ax.set(S.shift[0], S.shift[1], S.shift[2]).multiplyScalar(lo));
    this.ops(S.pose, lo, up);
    // ---- idle life ----
    const br = Math.sin(t * (Math.PI * 2 / 3.6)) * L.breath;                 // breath (3.6 s)
    this.rot('chest', [1, 0, 0], -0.014 * br * up); this.rot('neck', [1, 0, 0], 0.006 * br * up);
    this.ops(shoulder('L', 0.02 * br, 0), lo, up); this.ops(shoulder('R', 0.02 * br, 0), lo, up);
    const sw = Math.sin(t * (Math.PI * 2 / 5.3)) * L.sway;                   // weight shift (5.3 s)
    // (everything is FK: the pelvis barely moves so the feet stay put; the shift reads through the torso and shoulders)
    this.rot('hips', [0, 0, 1], 0.008 * sw * lo); this.rot('spine', [0, 0, 1], 0.022 * sw * up); this.rot('chest', [0, 0, 1], 0.012 * sw * up); this.rot('head', [0, 0, 1], -0.014 * sw * up);
    this.rot('hips', [0, 1, 0], 0.008 * Math.sin(t * 0.9) * L.sway * lo); this.rot('chest', [0, 1, 0], 0.016 * Math.sin(t * 0.9) * L.sway * up);
    if (L.bob > 0) {                                                            // knee pump: feet rise straight up (thigh fwd + shin flex cancel in z), the plant drops the body = bob
      const bb = (0.5 - 0.5 * Math.cos(t * Math.PI * 2 * L.bobHz)) * L.bob;
      for (const s of ['L', 'R'] as Side[]) { this.ops(shin(s, 0.07 * bb), lo, up); this.ops(leg(s, 0.031 * bb), lo, up); }
      this.rot('chest', [1, 0, 0], -0.015 * bb * up); this.rot('spine', [1, 0, 0], 0.01 * bb * up);
    }
    // micro-adjustment every 2.2-5 s: fist re-clench, rear shoulder roll, glance, lead-hand flex, breath catch
    if (L.fidget > 0) {
      if (this.fidgetK >= 1 && t >= this.fidgetAt) { this.fidgetK = 0; this.fidgetKind = Math.floor(h01(this.fidgetN) * 5); this.fidgetN++; this.fidgetAt = t + 2.2 + 2.8 * h01(this.fidgetN * 7 + 3); }
      if (this.fidgetK < 1) {
        this.fidgetK = Math.min(1, this.fidgetK + dt / 0.6);
        const e = Math.sin(this.fidgetK * Math.PI) * L.fidget;
        switch (this.fidgetKind) {
          case 0: for (const s of ['L', 'R'] as Side[]) { this.ops(curl(s, 0.4 * e), lo, up); this.ops(fore(s, 0.05 * e), lo, up); } break;
          case 1: this.ops(shoulder('R', 0.16 * e, 0.12 * e), lo, up); this.rot('chest', [0, 0, 1], 0.02 * e * up); break;
          case 2: this.rot('head', [0, 1, 0], 0.14 * e * up); this.rot('neck', [0, 1, 0], 0.06 * e * up); break;
          case 3: this.ops(hand('L', 0.3 * e), lo, up); this.ops(curl('L', -0.25 * e), lo, up); break;
          default: this.rot('chest', [1, 0, 0], -0.045 * e * up); this.ops(shoulder('L', 0.05 * e), lo, up); this.ops(shoulder('R', 0.05 * e), lo, up);
        }
      }
    }
    // cloth: cape + split-tail scarf sway, and a settle swing when he comes to a stop (lower weight rising past 0.5)
    if (L.cloth > 0) {
      if (lo > 0.5 && this.lastLo <= 0.5) this.settleT = 0;
      this.settleT += dt;
      const settle = Math.exp(-this.settleT * 1.9) * Math.sin(this.settleT * 7.5) * -0.38;
      for (let k = 0; k < 4; k++) {
        const g = (0.45 + 0.2 * k) * L.cloth;
        this.rot(`mantle.${k}`, [1, 0, 0], (Math.sin(t * 1.7 + k * 0.9) * 0.018 + settle) * g * up);
        this.rot(`mantle.${k}`, [0, 0, 1], Math.sin(t * 1.1 + k * 0.7) * 0.012 * g * up);
        this.rot(`scarf.L.${k}`, [1, 0, 0], (Math.sin(t * 2.1 + k * 1.1) * 0.02 + settle * 0.8) * g * up);
        this.rot(`scarf.R.${k}`, [1, 0, 0], (Math.sin(t * 1.9 + k * 1.1 + 1.3) * 0.02 + settle * 0.8) * g * up);
        this.rot(`scarf.L.${k}`, [0, 0, 1], Math.sin(t * 1.3 + k) * 0.014 * g * up); this.rot(`scarf.R.${k}`, [0, 0, 1], Math.sin(t * 1.4 + k + 2) * 0.014 * g * up);
      }
    }
    this.lastLo = lo;
  }
}
